import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Download, Mail, Phone, Search, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "instone_admin_token";

const formatBytes = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LoginScreen = ({ onLoggedIn }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLoggedIn(data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Incorrect password.";
      toast.error(typeof detail === "string" ? detail : "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo.png" alt="In Stone Supplies" className="h-12 w-auto" />
        </div>
        <span className="label-overline">Internal · Admin</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-light mt-4 leading-tight text-[#1A1A1A]">
          Sign in to <span className="italic text-[#8C7A6B]">applications.</span>
        </h1>
        <p className="mt-4 text-[#5C5C5C]">
          Enter the admin password to view received job applications and download resumes.
        </p>
        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <div>
            <Label htmlFor="admin_password" className="label-overline">Password</Label>
            <Input
              id="admin_password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoFocus
              className="mt-2 rounded-none border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] h-12"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#1A1A1A] text-[#F9F8F6] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#3a3a3a] transition-colors disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <><ShieldCheck size={16} /> Sign In</>}
          </button>
        </form>
        <p className="mt-10 text-xs text-[#8C7A6B] tracking-wider">
          Authorized personnel only. All access is logged.
        </p>
      </div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/applications`, { headers: authHeaders });
        if (!cancelled) setApps(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          onLogout();
        } else {
          toast.error("Unable to load applications.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders, onLogout]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      if (positionFilter !== "all" && a.position !== positionFilter) return false;
      if (!q) return true;
      return [a.full_name, a.email, a.phone, a.position].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [apps, query, positionFilter]);

  const counts = useMemo(() => {
    return {
      total: apps.length,
      logistics: apps.filter((a) => a.position === "Logistics Manager").length,
      driver: apps.filter((a) => a.position === "Driver").length,
    };
  }, [apps]);

  const downloadResume = async (appRow) => {
    try {
      const res = await axios.get(`${API}/admin/applications/${appRow.id}/resume`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", appRow.resume_filename || `resume_${appRow.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Download failed.";
      toast.error(typeof detail === "string" ? detail : "Download failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8]">
        <div className="container-wide flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="In Stone Supplies" className="h-10 w-auto" />
            <span className="hidden md:inline label-overline">Admin · Applications</span>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#1A1A1A] text-[#1A1A1A] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#1A1A1A] hover:text-[#F9F8F6] transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>

      <main className="container-wide py-10 md:py-14">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-px bg-[#E5E0D8] border border-[#E5E0D8] mb-10">
          {[
            { label: "Total received", value: counts.total },
            { label: "Logistics Manager", value: counts.logistics },
            { label: "Driver", value: counts.driver },
          ].map((s) => (
            <div key={s.label} className="bg-white p-6 md:p-8">
              <p className="label-overline">{s.label}</p>
              <p className="font-heading text-4xl md:text-5xl font-light mt-3 text-[#1A1A1A]">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8C7A6B]" />
            <Input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, phone, or position…"
              className="pl-11 rounded-none border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] h-12"
            />
          </div>
          <div className="flex gap-px bg-[#E5E0D8] border border-[#E5E0D8]">
            {[
              { v: "all", label: "All" },
              { v: "Logistics Manager", label: "Logistics" },
              { v: "Driver", label: "Driver" },
            ].map((p) => (
              <button
                key={p.v}
                onClick={() => setPositionFilter(p.v)}
                className={`px-5 h-12 text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${
                  positionFilter === p.v
                    ? "bg-[#1A1A1A] text-[#F9F8F6]"
                    : "bg-white text-[#1A1A1A] hover:bg-[#F9F8F6]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E0D8]">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E5E0D8] label-overline">
            <div className="col-span-3">Applicant</div>
            <div className="col-span-2">Position</div>
            <div className="col-span-3">Contact</div>
            <div className="col-span-2">Submitted</div>
            <div className="col-span-2 text-right">Resume</div>
          </div>

          {loading ? (
            <div className="p-16 flex items-center justify-center text-[#5C5C5C]">
              <Loader2 className="animate-spin" /> <span className="ml-3">Loading applications…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <p className="font-heading text-2xl text-[#1A1A1A] font-light">No applications match.</p>
              <p className="text-[#5C5C5C] text-sm mt-2">Try clearing filters or searching a different name.</p>
            </div>
          ) : (
            <ul>
              {filtered.map((a) => (
                <li
                  key={a.id}
                  data-testid={`admin-application-row-${a.id}`}
                  className="grid md:grid-cols-12 gap-4 px-6 py-5 border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#FBFAF8] transition-colors"
                >
                  <div className="md:col-span-3">
                    <button
                      onClick={() => setSelected(a)}
                      className="text-left font-heading text-lg text-[#1A1A1A] hover:text-[#8C7A6B] transition-colors"
                    >
                      {a.full_name}
                    </button>
                  </div>
                  <div className="md:col-span-2 text-sm flex items-center text-[#1A1A1A]">
                    {a.position}
                  </div>
                  <div className="md:col-span-3 text-sm text-[#5C5C5C] flex flex-col">
                    <a href={`mailto:${a.email}`} className="inline-flex items-center gap-2 hover:text-[#1A1A1A]">
                      <Mail size={12} /> {a.email}
                    </a>
                    <a href={`tel:${a.phone}`} className="inline-flex items-center gap-2 hover:text-[#1A1A1A]">
                      <Phone size={12} /> {a.phone}
                    </a>
                  </div>
                  <div className="md:col-span-2 text-sm text-[#5C5C5C] flex items-center">
                    {formatDate(a.submitted_at)}
                  </div>
                  <div className="md:col-span-2 flex md:justify-end items-center">
                    {a.has_resume ? (
                      <button
                        data-testid={`admin-download-resume-${a.id}`}
                        onClick={() => downloadResume(a)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A1A1A] text-[#F9F8F6] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#3a3a3a] transition-colors"
                      >
                        <Download size={13} /> {formatBytes(a.resume_size_bytes)}
                      </button>
                    ) : (
                      <span className="text-xs text-[#8C7A6B] tracking-wider uppercase">No file</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white w-full max-w-lg h-full overflow-y-auto p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="label-overline">{selected.position}</span>
                <h3 className="font-heading text-3xl mt-2 font-light text-[#1A1A1A]">
                  {selected.full_name}
                </h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#8C7A6B] hover:text-[#1A1A1A]">✕</button>
            </div>
            <div className="mt-8 space-y-5 text-sm">
              <div>
                <p className="label-overline">Email</p>
                <a className="block mt-1 text-[#1A1A1A] hover:text-[#8C7A6B]" href={`mailto:${selected.email}`}>
                  {selected.email}
                </a>
              </div>
              <div>
                <p className="label-overline">Phone</p>
                <a className="block mt-1 text-[#1A1A1A] hover:text-[#8C7A6B]" href={`tel:${selected.phone}`}>
                  {selected.phone}
                </a>
              </div>
              <div>
                <p className="label-overline">Submitted</p>
                <p className="mt-1 text-[#5C5C5C]">{formatDate(selected.submitted_at)}</p>
              </div>
              <div>
                <p className="label-overline">Cover note</p>
                <p className="mt-1 text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">
                  {selected.cover_message || <em className="text-[#8C7A6B]">No cover note provided.</em>}
                </p>
              </div>
              <div>
                <p className="label-overline">Resume</p>
                {selected.has_resume ? (
                  <button
                    onClick={() => downloadResume(selected)}
                    className="mt-2 inline-flex items-center gap-2 px-5 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#3a3a3a] transition-colors"
                  >
                    <Download size={13} /> Download · {formatBytes(selected.resume_size_bytes)}
                  </button>
                ) : (
                  <p className="mt-1 text-[#8C7A6B]">No file attached.</p>
                )}
              </div>
              <div className="pt-4 border-t border-[#E5E0D8]">
                <p className="label-overline">Application ID</p>
                <p className="mt-1 font-mono text-xs text-[#5C5C5C] break-all">{selected.id}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  // Validate token on mount
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
      } catch {
        onLogout();
      }
    })();
  }, [token]);

  if (!token) return <LoginScreen onLoggedIn={setToken} />;
  return <Dashboard token={token} onLogout={onLogout} />;
};

export default Admin;
